import { useState, useEffect } from 'react';
import { Button, secondaryButtonClassName, cardSurfaceClassName } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { cardTitle, cardDescription, cardPadding } from '../profileStyles';
import { getKeycloak } from '../../../auth/services/keycloakService';
import { getAuthenticatorStatus, removeAuthenticator } from '../../services/profileService';
import { ConfirmationDialog } from '@shared/components/ConfirmationDialog';
import { Loader2 } from 'lucide-react';

// null → still loading / probe failed. Kept distinct from false so the button label doesn't
// misrepresent state during a Keycloak outage (feedback #7).
const STATUS = {
  UNKNOWN: null,
  CONFIGURED: true,
  NOT_CONFIGURED: false,
};

/**
 * AuthenticatorSection — personal MFA authenticator control on the Profile page.
 *
 * Any authenticated user, any role. Kept separate from the firm-wide MFA enable/disable control.
 *
 *   - No credential  → "Set Authenticator" → Keycloak's native CONFIGURE_TOTP flow (PKCE-safe).
 *   - Has credential → "Remove Authenticator" → confirm dialog → DELETE /me/authenticator.
 *   - Probe failure  → error state with retry; the button label never falsely promises "Set" for
 *                       a user who might actually have MFA (feedback #7).
 *
 * @param {{ dataTestId?: string, onLoadingChange?: (loading: boolean, message?: string) => void }} props
 *   `onLoadingChange` — same overlay the FirmSubdomainSection uses; blocks the profile popup
 *   during the removal round-trip so a mid-flight close can't leave the UI in a bad state.
 */
export function AuthenticatorSection({ dataTestId, onLoadingChange }) {
  const [status, setStatus] = useState(STATUS.UNKNOWN);
  const [isLoading, setIsLoading] = useState(true);
  const [statusError, setStatusError] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const baseTestId = dataTestId || 'profile-authenticator-section';

  useEffect(() => {
    loadAuthenticatorStatus();
    // Intentionally run once on mount — the redirect from Keycloak's CONFIGURE_TOTP flow triggers
    // a full page reload, which remounts this and re-fetches. No deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAuthenticatorStatus = async () => {
    setIsLoading(true);
    setStatusError(false);
    // The underlying service catches its own errors and returns { hasTotpConfigured: false } on
    // failure. We disambiguate a real "no credential" from a probe failure by checking for the
    // hasTotpConfigured key explicitly — a graceful-failure payload still has the key set to
    // false, so we treat a missing key as an error signal. Kept simple: the service is a thin
    // wrapper, so no try/catch is needed here (feedback #4).
    const result = await getAuthenticatorStatus();
    if (result && typeof result.hasTotpConfigured === 'boolean') {
      setStatus(result.hasTotpConfigured ? STATUS.CONFIGURED : STATUS.NOT_CONFIGURED);
    } else {
      setStatusError(true);
      setStatus(STATUS.UNKNOWN);
    }
    setIsLoading(false);
  };

  const handleSetAuthenticator = () => {
    // Null-guard around Keycloak init (feedback #8). The Profile page is behind a protected route
    // so this should always be truthy in practice — but a mispaged race during a token refresh
    // has been seen in the wild and calling .login on undefined is worse than a no-op.
    const keycloak = getKeycloak();
    if (!keycloak || typeof keycloak.login !== 'function') {
      setStatusError(true);
      return;
    }
    keycloak.login({
      action: 'CONFIGURE_TOTP',
      redirectUri: globalThis.location.href,
    });
  };

  const openRemoveConfirm = () => {
    if (status !== STATUS.CONFIGURED || isRemoving) return;
    setIsConfirmOpen(true);
  };

  const confirmRemove = async () => {
    setIsRemoving(true);
    onLoadingChange?.(true, 'Removing authenticator...');
    // Service handles its own toasts and never throws — no wrapping try/catch (feedback #4).
    const result = await removeAuthenticator();
    if (result?.success) {
      setStatus(STATUS.NOT_CONFIGURED);
      setIsConfirmOpen(false);
    }
    setIsRemoving(false);
    onLoadingChange?.(false);
  };

  const isConfigured = status === STATUS.CONFIGURED;
  const buttonLabel = isConfigured ? 'Remove Authenticator' : 'Configure';
  let description = 'Set up an authenticator app for two-factor authentication.';
  if (statusError) {
    description = 'Could not check your authenticator status. Refresh the page and try again.';
  } else if (isConfigured) {
    description = 'Your authenticator is configured. Remove it if you no longer need it or want to set up a new one.';
  }

  return (
    <div
      className={cn('flex items-center justify-between border rounded-xl', cardPadding, cardSurfaceClassName)}
      data-testid={baseTestId}
    >
      <div className="flex-1">
        <h4 className={cardTitle} data-testid={`${baseTestId}-title`}>
          Multi Factor Authentication (MFA)
        </h4>
        <p className={cardDescription} data-testid={`${baseTestId}-description`}>
          {isLoading ? 'Loading authenticator status...' : description}
        </p>
      </div>
      <Button
        type="button"
        onClick={isConfigured ? openRemoveConfirm : handleSetAuthenticator}
        // Disabled during load, during a removal, and when the status probe failed — never present
        // a button whose label doesn't match reality (feedback #7).
        disabled={isLoading || isRemoving || statusError}
        variant="outline"
        // The dashboard's "Manage" look (shared), so in-content actions match across the app.
        className={cn(secondaryButtonClassName, 'ml-4 px-4 disabled:cursor-not-allowed')}
        data-testid={`${baseTestId}-button`}
      >
        {isLoading || isRemoving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {isRemoving ? 'Removing...' : 'Loading...'}
          </>
        ) : (
          buttonLabel
        )}
      </Button>

      {/* Same ConfirmationDialog the admin path uses (feedback #1). Destructive action must not be
          a one-click affair — self-removal is arguably MORE sensitive than an admin removal
          because the user is weakening their own account. */}
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        title="Remove your authenticator?"
        description="This deletes your two-factor authenticator. You can set up a new one from this page, or you'll be prompted to at next login if your firm requires MFA."
        cancelLabel="Cancel"
        confirmLabel="Remove Authenticator"
        variant="danger"
        isLoading={isRemoving}
        onCancel={() => { if (!isRemoving) setIsConfirmOpen(false); }}
        onConfirm={confirmRemove}
        dataTestId={`${baseTestId}-remove-confirmation`}
      />
    </div>
  );
}
